import { Button, Flex, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { SecurityFormData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/types.ts';
import React, { useRef } from 'react';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { useSecuritiesTableState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/components/SecuritiesTable/useSecuritiesTableState.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

interface Props {
	securities: SecurityFormData[];
	entityName?: string;
	onEditClicked: (security: SecurityFormData) => void;
	onAddClicked: () => void;
	onValuationSettingsClicked: () => void;
	onToggleVesting: (security: SecurityFormData) => void;
	valuationDate?: Date | null;
	shareWithInvestors?: boolean;
	style?: React.CSSProperties;
}

export const SecuritiesTable = ({
	securities,
	entityName,
	onEditClicked,
	onAddClicked,
	onValuationSettingsClicked,
	onToggleVesting,
	valuationDate,
	shareWithInvestors,
	style,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tableRef = useRef<HTMLDivElement | null>(null);

	const { columns, mantineTableDataOperations } = useSecuritiesTableState({
		onEdit: onEditClicked,
		onToggleVesting,
	});

	const formattedValuationDate = formatDateToUI(valuationDate, DATE_FORMATS.UI);

	return (
		<Flex direction="column" w="100%" style={style}>
			<Flex justify="space-between" align="center" pt="1rem" pb="0.5rem">
				<Text size="lg" fw={600}>
					{entityName}
				</Text>

				<Flex align="center" gap="xs">
					{valuationDate && (
						<Flex align="center" gap="md" mr="sm">
							<Flex align="center" gap="xs">
								<Text size="sm" c="gray.9">
									{tMipManage('root.securities.button.valuation_date')}
								</Text>
								<Text size="sm">{formattedValuationDate}</Text>
							</Flex>

							<Flex align="center" gap="xs">
								<Text size="sm" c="gray.9">
									{tMipManage('root.securities.button.investor_share')}
								</Text>
								<Text size="sm">{shareWithInvestors ? 'Yes' : 'No'}</Text>
							</Flex>
						</Flex>
					)}

					<Button
						onClick={onValuationSettingsClicked}
						variant="default"
						leftSection={<Icon variant="money-v3" size="lg" />}
					>
						{tMipManage('root.securities.button.set_valuation')}
					</Button>

					<Button
						onClick={onAddClicked}
						data-testid={TEST_ELEMENT_IDS.MIP_PROGRAMME_SECURITY_ADD_BUTTON}
						leftSection={<Icon variant="plus" size="lg" />}
					>
						{tMipManage('root.securities.button.add_security')}
					</Button>
				</Flex>
			</Flex>
			<MantineTable<SecurityFormData>
				getRowId={(row) => row.id || ''}
				tableRef={tableRef}
				columns={columns}
				dataOperations={mantineTableDataOperations}
				data={securities}
				initialState={{
					columnVisibility: {
						id: false,
						createdAt: false,
					},
				}}
				enableRowActions={false}
				enablePagination={false}
				enableTopToolbar={false}
				enableFilters={false}
				enableGlobalFilter={false}
				enableRowSelection={false}
				manualPagination={false}
				manualSorting={false}
			/>
		</Flex>
	);
};
